import { useEffect, useMemo, useState } from "react";
import { Edit2, Plus, Trash2, BookMarked } from "lucide-react";
import { useTenant } from "@/contexts/TenantContext";
import { useToast } from "@/contexts/ToastContext";
import { AppLayout } from "@/components/layout/AppLayout";
import { PageHeader } from "@/components/layout/PageHeader";
import { PageContainer } from "@/components/layout/PageContainer";
import {
  CirculationPolicyDialog,
  type PolicyFormState,
} from "@/components/settings/CirculationPolicyDialog";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { DataTable, type DataTableColumn } from "@/components/ui/data-table";
import { EmptyState } from "@/components/ui/empty-state";
import { LoadingSpinner } from "@/components/ui/loading-spinner";
import { circulationApi } from "@/lib/api/circulation";
import api from "@/lib/api";

interface Policy {
  id: string;
  patronTypeId: string;
  itemTypeId: string;
  branchId: string | null;
  loanPeriodDays: number;
  renewalLimit: number;
  fineRatePerDay: number | string;
  gracePeriodDays: number;
  maxFineAmount: number | string | null;
  isActive: boolean;
  patronType: { id: string; name: string };
  itemType: { id: string; name: string; code: string };
  branch: { id: string; name: string; code: string } | null;
}

type TypeOption = { id: string; name: string; code?: string };

const EMPTY_FORM: PolicyFormState = {
  patronTypeId: "",
  itemTypeId: "",
  branchId: "",
  loanPeriodDays: 14,
  renewalLimit: 2,
  fineRatePerDay: 0,
  gracePeriodDays: 0,
  maxFineAmount: "",
  isActive: true,
};

const CirculationPoliciesPage = () => {
  const { tenant } = useTenant();
  const toast = useToast();
  const [policies, setPolicies] = useState<Policy[]>([]);
  const [patronTypes, setPatronTypes] = useState<TypeOption[]>([]);
  const [itemTypes, setItemTypes] = useState<TypeOption[]>([]);
  const [branches, setBranches] = useState<TypeOption[]>([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [form, setForm] = useState<PolicyFormState>(EMPTY_FORM);

  useEffect(() => {
    if (tenant?.id) void loadData();
  }, [tenant?.id]);

  const loadData = async () => {
    setLoading(true);
    try {
      const [policiesRes, ptRes, itRes, brRes] = await Promise.all([
        circulationApi.getPolicies(),
        api.get("/patron-types"),
        api.get("/item-types"),
        api.get("/branches"),
      ]);
      setPolicies(policiesRes);
      setPatronTypes(
        (ptRes.data || [])
          .filter((pt: { isActive?: boolean }) => pt.isActive)
          .map((pt: { id: string; name: string }) => ({ id: pt.id, name: pt.name }))
      );
      setItemTypes(
        (itRes.data || []).map((it: { id: string; name: string; code?: string }) => ({
          id: it.id,
          name: it.name,
          code: it.code,
        }))
      );
      setBranches(
        (brRes.data || [])
          .filter((b: { isActive?: boolean }) => b.isActive !== false)
          .map((b: { id: string; name: string; code?: string }) => ({
            id: b.id,
            name: b.name,
            code: b.code,
          }))
      );
    } catch {
      toast.error("Failed to load circulation policies");
    } finally {
      setLoading(false);
    }
  };

  const openNew = () => {
    setEditingId(null);
    setForm(EMPTY_FORM);
    setDialogOpen(true);
  };

  const openEdit = (p: Policy) => {
    setEditingId(p.id);
    setForm({
      patronTypeId: p.patronTypeId,
      itemTypeId: p.itemTypeId,
      branchId: p.branchId || "",
      loanPeriodDays: p.loanPeriodDays,
      renewalLimit: p.renewalLimit,
      fineRatePerDay: Number(p.fineRatePerDay),
      gracePeriodDays: p.gracePeriodDays,
      maxFineAmount: p.maxFineAmount != null ? String(p.maxFineAmount) : "",
      isActive: p.isActive,
    });
    setDialogOpen(true);
  };

  const handleSave = async () => {
    if (!form.patronTypeId || !form.itemTypeId) {
      toast.error("Please select both Patron Type and Item Type");
      return;
    }
    setSaving(true);
    try {
      await circulationApi.upsertPolicy({
        patronTypeId: form.patronTypeId,
        itemTypeId: form.itemTypeId,
        branchId: form.branchId || undefined,
        loanPeriodDays: form.loanPeriodDays,
        renewalLimit: form.renewalLimit,
        fineRatePerDay: form.fineRatePerDay,
        gracePeriodDays: form.gracePeriodDays,
        maxFineAmount: form.maxFineAmount ? parseFloat(form.maxFineAmount) : undefined,
        isActive: form.isActive,
      });
      toast.success(editingId ? "Policy updated" : "Policy created");
      setDialogOpen(false);
      await loadData();
    } catch (err: unknown) {
      const message =
        err && typeof err === "object" && "response" in err
          ? (err as { response?: { data?: { message?: string } } }).response?.data?.message
          : undefined;
      toast.error(message || "Failed to save policy");
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (policyId: string) => {
    if (!confirm("Delete this circulation policy?")) return;
    try {
      await circulationApi.deletePolicy(policyId);
      toast.success("Policy deleted");
      await loadData();
    } catch {
      toast.error("Failed to delete policy");
    }
  };

  const columns: DataTableColumn<Policy>[] = useMemo(
    () => [
      {
        id: "patron",
        header: "Patron type",
        cell: (p) => <span className="font-medium text-slate-900">{p.patronType.name}</span>,
      },
      { id: "item", header: "Item type", cell: (p) => p.itemType.name },
      {
        id: "branch",
        header: "Branch",
        hideOnMobile: true,
        cell: (p) => <span className="text-slate-500">{p.branch?.name ?? "All"}</span>,
      },
      {
        id: "loan",
        header: "Loan",
        align: "center",
        headerAlign: "center",
        cell: (p) => `${p.loanPeriodDays}d`,
      },
      {
        id: "renew",
        header: "Renewals",
        align: "center",
        headerAlign: "center",
        hideOnMobile: true,
        cell: (p) => p.renewalLimit,
      },
      {
        id: "fine",
        header: "Fine/day",
        align: "right",
        headerAlign: "right",
        cell: (p) => `₦${Number(p.fineRatePerDay).toFixed(2)}`,
      },
      {
        id: "status",
        header: "Status",
        align: "center",
        headerAlign: "center",
        cell: (p) => (
          <Badge variant={p.isActive ? "default" : "secondary"} className="text-xs">
            {p.isActive ? "Active" : "Inactive"}
          </Badge>
        ),
      },
      {
        id: "actions",
        header: "",
        align: "right",
        headerAlign: "right",
        width: "w-24",
        cell: (p) => (
          <div className="flex items-center justify-end gap-1">
            <Button variant="ghost" size="icon" onClick={() => openEdit(p)} aria-label="Edit">
              <Edit2 className="h-3.5 w-3.5" />
            </Button>
            <Button
              variant="ghost"
              size="icon"
              onClick={() => handleDelete(p.id)}
              aria-label="Delete"
            >
              <Trash2 className="h-3.5 w-3.5 text-red-500" />
            </Button>
          </div>
        ),
      },
    ],
    []
  );

  return (
    <AppLayout>
      <PageContainer>
        <PageHeader
          title="Circulation Policies"
          description="Override loan, renewal, and fine rules for specific patron type + item type pairs."
          breadcrumbItems={[{ label: "Setup", href: "/setup" }, { label: "Circulation Policies" }]}
          showSearch={false}
          titleSize="md"
          actions={
            <Button onClick={openNew} size="sm" className="gap-1.5">
              <Plus className="h-4 w-4" />
              Add policy
            </Button>
          }
        />

        {loading ? (
          <div className="flex justify-center py-16">
            <LoadingSpinner size="lg" label="Loading policies…" />
          </div>
        ) : (
          <DataTable
            columns={columns}
            data={policies}
            rowKey={(p) => p.id}
            emptyState={
              <EmptyState
                icon={<BookMarked className="h-6 w-6 text-slate-400" />}
                title="No circulation policies yet"
                description="Defaults from Patron Type and Item Type settings apply until you add overrides."
                action={
                  <Button onClick={openNew} size="sm" className="gap-1.5">
                    <Plus className="h-4 w-4" />
                    Create first policy
                  </Button>
                }
              />
            }
            mobileCard={(p) => (
              <div className="rounded-lg border border-slate-200 bg-white p-4 space-y-2">
                <div className="flex items-start justify-between gap-2">
                  <div>
                    <p className="font-medium text-slate-900">{p.patronType.name}</p>
                    <p className="text-sm text-slate-500">{p.itemType.name}</p>
                  </div>
                  <Badge variant={p.isActive ? "default" : "secondary"} className="text-xs">
                    {p.isActive ? "Active" : "Inactive"}
                  </Badge>
                </div>
                <p className="text-sm text-slate-600">
                  {p.loanPeriodDays}d loan · {p.renewalLimit} renewals · ₦
                  {Number(p.fineRatePerDay).toFixed(2)}/day
                </p>
                <div className="flex justify-end gap-1 pt-1">
                  <Button variant="outline" size="sm" onClick={() => openEdit(p)}>
                    Edit
                  </Button>
                  <Button variant="outline" size="sm" onClick={() => handleDelete(p.id)}>
                    Delete
                  </Button>
                </div>
              </div>
            )}
          />
        )}

        <CirculationPolicyDialog
          open={dialogOpen}
          editing={!!editingId}
          saving={saving}
          form={form}
          patronTypes={patronTypes}
          itemTypes={itemTypes}
          branches={branches}
          onOpenChange={setDialogOpen}
          onChange={setForm}
          onSave={handleSave}
        />
      </PageContainer>
    </AppLayout>
  );
};

export default CirculationPoliciesPage;
